/**
 * seoPages.ts  -  Content for the public SEO landing pages.
 *
 * Each entry renders at /{slug} via SeoLandingPage.tsx and is listed in
 * public/sitemap.xml. Adding a page means adding one entry here plus one
 * sitemap line - no new component.
 *
 * Copy rules: no long dashes, UK spelling, claims must be true of the product.
 */

export interface SeoSection {
  heading: string;
  body: string;
}

export interface SeoPage {
  slug: string;
  /** <title> - keep under ~60 chars */
  title: string;
  /** meta description - keep under ~155 chars */
  description: string;
  /** H1 */
  h1: string;
  /** Lead paragraph under the H1 */
  intro: string;
  sections: SeoSection[];
  /** Short FAQ - also emitted as FAQPage JSON-LD */
  faqs: { q: string; a: string }[];
  ctaLabel: string;
}

export const SEO_PAGES: SeoPage[] = [
  {
    slug: "phonics-screening-check",
    title: "Year 1 Phonics Screening Check: Practice and Tracking",
    description:
      "Prepare your Year 1 class for the Phonics Screening Check. Practise real and pseudo words, track every child's decoding, and spot gaps before June.",
    h1: "Year 1 Phonics Screening Check practice and tracking",
    intro:
      "The Phonics Screening Check asks every Year 1 child to decode 40 words, half of them pseudo words. Litlings gives you daily practice activities and a per-child record of which graphemes are secure, so you know who needs keep-up long before June.",
    sections: [
      {
        heading: "Know who is on track, without extra marking",
        body: "Children complete short decoding tasks on any device. Reading attempts are captured as audio and pre-scored automatically, so you confirm rather than mark from scratch. The class view shows who is secure, who is borderline, and who needs immediate keep-up.",
      },
      {
        heading: "Practise real and pseudo words",
        body: "Activities cover both real words and pseudo words in the same format children meet in the check, so the format itself is never the barrier on the day.",
      },
      {
        heading: "Evidence you can show",
        body: "Export a per-child PDF or a class CSV showing grapheme mastery over time. Useful for pupil progress meetings, and for showing a subject lead or inspector how you identified and closed gaps.",
      },
    ],
    faqs: [
      {
        q: "When is the Phonics Screening Check?",
        a: "It normally takes place in the second week of June in Year 1, with a repeat check in Year 2 for children who did not meet the standard.",
      },
      {
        q: "Does Litlings replace our phonics scheme?",
        a: "No. Litlings is built around the DfE Letters and Sounds progression and is designed to sit alongside your existing scheme, adding practice, automatic marking and tracking.",
      },
      {
        q: "How much does it cost?",
        a: "Free forever for up to 40 children, with as many classes or groups as you like. A whole school is from GBP 300 a year, with a 60-day free trial and no credit card required.",
      },
    ],
    ctaLabel: "Start free with one class",
  },
  {
    slug: "phonics-assessment-tracker",
    title: "Phonics Assessment Tracker for Primary Schools",
    description:
      "Replace the phonics tracking spreadsheet. Automatic per-child grapheme mastery, class heatmaps and exportable evidence for pupil progress meetings.",
    h1: "A phonics assessment tracker that fills itself in",
    intro:
      "Most phonics tracking still happens in a spreadsheet somebody updates by hand on a Friday. Litlings records mastery as children work, so the tracker is always current and nobody has to maintain it.",
    sections: [
      {
        heading: "Per-child grapheme mastery",
        body: "Every submission updates that child's record for the specific grapheme practised. The result is a heatmap showing exactly which sounds are secure and which need revisiting, per child and across the class.",
      },
      {
        heading: "Built for keep-up, not just recording",
        body: "Tracking is only useful if it changes what you teach tomorrow. The class view sorts children by who needs attention now, so daily keep-up groups take seconds to form.",
      },
      {
        heading: "Export for meetings and inspection",
        body: "Per-child PDF report cards and whole-class CSV exports, generated on demand. No rebuilding the data into a different format for someone else's template.",
      },
    ],
    faqs: [
      {
        q: "Do we have to enter assessment data manually?",
        a: "No. Mastery updates automatically from the work children complete. You can always override a score if your professional judgement differs.",
      },
      {
        q: "Can our subject lead see the whole school?",
        a: "Yes. School administrators see every class, and multi-academy trusts get a group dashboard across all their schools.",
      },
    ],
    ctaLabel: "See the tracker free",
  },
  {
    slug: "phonics-homework",
    title: "Phonics Homework Ideas That Mark Themselves",
    description:
      "Set phonics homework in seconds and get it back pre-marked. Parents see what to do, children practise on any phone, and you confirm rather than mark.",
    h1: "Phonics homework that comes back already marked",
    intro:
      "Setting phonics homework is quick. Chasing it, checking it and recording it is not. Litlings handles the middle part so the workload stays proportionate.",
    sections: [
      {
        heading: "Set it in seconds",
        body: "Pick a lesson, set it as homework, choose a due date. Every child in the class gets it, and their parent gets a notification with a direct link.",
      },
      {
        heading: "Children practise on any device",
        body: "Activities run in a browser on a low-cost Android phone over a slow connection. No app install, no school device needed.",
      },
      {
        heading: "It comes back pre-scored",
        body: "Photo and audio submissions are pre-scored automatically. You review the queue, confirm or adjust the stars, add a voice note if you want, and move on.",
      },
    ],
    faqs: [
      {
        q: "What if a family has no device or internet at home?",
        a: "Activities work offline once loaded and sync when a connection returns. Printable resources are also available for families with no device at all.",
      },
      {
        q: "Do parents need to create an account?",
        a: "Parents get a read-only view on the free plan, and full access with messaging and progress on paid plans.",
      },
    ],
    ctaLabel: "Set your first homework free",
  },
  {
    slug: "reception-phonics",
    title: "Reception Phonics Activities and Progress Tracking",
    description:
      "Phase 1 and Phase 2 phonics for Reception. Play-based activities on any device, automatic progress tracking, and updates parents actually read.",
    h1: "Reception phonics, tracked without the paperwork",
    intro:
      "Reception phonics is mostly oral, practical and fast moving. Litlings adds short digital activities for Phase 1 and Phase 2 and quietly records what each child can do, so your evidence builds itself.",
    sections: [
      {
        heading: "Phase 1 and Phase 2 activities",
        body: "Listening, oral blending, and the first grapheme-phoneme correspondences, presented as short games rather than worksheets.",
      },
      {
        heading: "Evidence without clipboards",
        body: "Each completed activity updates that child's record. At parents evening you have a picture of what is secure without having transcribed anything.",
      },
      {
        heading: "Parents can see and join in",
        body: "Parents get a weekly summary of what their child has practised, plus suggestions for what to do at home. It takes five minutes to read.",
      },
    ],
    faqs: [
      {
        q: "Is screen time appropriate in Reception?",
        a: "Activities are short by design and intended to supplement adult-led teaching, not replace it. Most tasks take under five minutes.",
      },
      {
        q: "Which phases are covered?",
        a: "Phases 1 and 2 are available now, with later phases rolling out. Pricing reflects what is currently available.",
      },
    ],
    ctaLabel: "Try it with your class",
  },
  {
    slug: "phonics-for-eal",
    title: "Phonics for EAL Learners in Primary School",
    description:
      "Phonics built for children learning English as an additional language, not only native speakers. Audio-first activities, and support on low-cost devices.",
    h1: "Phonics designed for EAL learners",
    intro:
      "Most phonics resources assume a child already speaks English fluently and is only learning to decode it. Litlings is built for classrooms where that is not true, including international and English-medium schools.",
    sections: [
      {
        heading: "Audio-first, not text-first",
        body: "Every activity leads with sound. Children hear the phoneme before they meet the grapheme, which matters when the spoken vocabulary is still developing.",
      },
      {
        heading: "Speaking practice that gets feedback",
        body: "Children record themselves reading. Attempts are pre-scored automatically and the teacher can add a voice note back, which is often more useful than a written comment for an EAL learner.",
      },
      {
        heading: "Works on the devices families actually have",
        body: "The activities run on older Android phones over slow connections, which matters a great deal outside well-funded school systems.",
      },
    ],
    faqs: [
      {
        q: "Is this only for UK schools?",
        a: "No. The progression follows the DfE Letters and Sounds sequence, but it is used in international and English-medium settings as well.",
      },
      {
        q: "Is the interface available in other languages?",
        a: "Localisation is in progress. Burmese is supported and further languages are being added.",
      },
    ],
    ctaLabel: "Start free",
  },
  {
    slug: "phonics-scheme-supplement",
    title: "Add Digital Practice and Tracking to Your Phonics Scheme",
    description:
      "Keep the phonics scheme your school already uses. Add digital practice, automatic marking and progress tracking on top, from GBP 300 a year.",
    h1: "Keep your scheme. Add the practice and the tracking.",
    intro:
      "Schools rarely want to change phonics scheme, and they should not have to. Litlings follows the DfE Letters and Sounds progression, so it sits alongside whichever validated scheme you already teach.",
    sections: [
      {
        heading: "Why schools add it",
        body: "The teaching and CPD usually already work. What is missing is practice children want to do at home, and tracking that does not depend on somebody maintaining a spreadsheet.",
      },
      {
        heading: "What it adds",
        body: "Digital practice activities, automatic pre-scoring of photo and audio work, per-child mastery tracking, parent updates, and exportable evidence.",
      },
      {
        heading: "What it does not do",
        body: "It does not tell you how to teach phonics or replace your scheme's lesson sequence. It is deliberately additive.",
      },
    ],
    faqs: [
      {
        q: "Is Litlings a DfE validated phonics programme?",
        a: "No. Litlings is not a validated systematic synthetic phonics programme and is not intended to replace one. It follows the DfE Letters and Sounds progression so it can sit alongside the validated scheme your school has chosen.",
      },
      {
        q: "Will it clash with our scheme's sequence?",
        a: "Assignments are set by you, so you control what children practise and when. You can align it to wherever your scheme currently is.",
      },
    ],
    ctaLabel: "Try alongside your scheme",
  },
];

SEO_PAGES.push({
  slug: "phonicsquest-is-now-litlings",
  title: "PhonicsQuest is now Litlings",
  description:
    "PhonicsQuest has been renamed Litlings. Same phonics teaching, marking and tracking, same logins, new name. Here is what changed and what did not.",
  h1: "PhonicsQuest is now Litlings",
  intro:
    "If you were looking for PhonicsQuest, you are in the right place. We renamed to Litlings. Your account, your classes and your children's progress are all unchanged.",
  sections: [
    {
      heading: "Why the name changed",
      body: "PhonicsQuest described one subject. We are now building beyond phonics into maths and wider primary literacy on the same platform, with the same teacher tools, so a phonics-only name no longer fitted. Litlings is the company; PhonicsQuest lives on as the name of the phonics product inside it.",
    },
    {
      heading: "What has not changed",
      body: "Your login still works. Your classes, students, join codes, assignments, submissions and progress records are all exactly as you left them. Nothing needs migrating and no data was moved.",
    },
    {
      heading: "What is new",
      body: "The platform now supports multiple subjects, so a class can be set up for phonics or maths. Existing classes were automatically kept as phonics classes.",
    },
  ],
  faqs: [
    {
      q: "Do I need a new account?",
      a: "No. Sign in at litlings.com with the same email and password you always used.",
    },
    {
      q: "Has pricing changed?",
      a: "No. Up to 40 children in as many classes or groups as you like stays free forever, and school plans start at GBP 300 a year.",
    },
    {
      q: "Is PhonicsQuest discontinued?",
      a: "No. PhonicsQuest is the phonics product within Litlings and is actively developed. The name Litlings covers the wider platform.",
    },
  ],
  ctaLabel: "Sign in to Litlings",
});

export function getSeoPage(slug: string): SeoPage | undefined {
  return SEO_PAGES.find((p) => p.slug === slug);
}
